<template>
  <el-dialog
    :visible.sync="visible"
    width="600px"
    :before-close="handleClose"
    custom-class="user-deletion-modal"
    align-center
  >
    <div slot="title">
        <h5 class="mb-0">
            Delete User
        </h5>
    </div>
    <div class="break-word">
      <!-- Step 1: Warning Message -->
      <div v-if="currentStep === 1" class="step-content">
        <h6>Are you sure you want to delete this user?</h6>

        <div class="deletion-warning">
          <p>Deleting a user will:</p>
          <ul>
            <li>Remove it from all reports and plots</li>
            <li>Delete all communication data</li>
            <li>Detach it from associated lines</li>
          </ul>
          <p>
            If you want to temporarily stop a user from getting calls, use the pause button.
          </p>

          <div class="learn-more" v-if="!isWhitelabelAccount">
            <p class="mb-0">Learn more <a class="text-decoration-underline el-link--primary" href="https://support.aloware.com/en/articles/9033784-billing-implication-of-paused-suspended-users" target="_blank">here</a>.</p>
          </div>

          <div class="license-info" v-if="hasPerSeatPlanUseCase">
            <p>You are removing a user from an assigned license. Your license will be available to transfer to a new user until the end of your billing period.</p>
            <p v-if="endOfBillingTerm" class="mb-0">
              End of billing term: {{ formatDate(endOfBillingTerm) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Step 2: Contact Reassignment -->
      <div v-if="currentStep === 2" class="step-content">
        <h6>Reassign User's Contacts (optional)</h6>
        <p>
          This user has {{ userContactsCount }} contact(s) that can be reassigned before deletion.
          <br/>
          Please select a user below if you want to reassign the contacts to a different user.
        </p>

        <div>
          <label class="d-block mb-2">Reassign contacts to:</label>
          <user-selector
            v-if="!isSuperAdmin"
            v-model="selectedReassignUser"
            clearable
            only-active
            style="width: 300px"
            class="pull-left"
            placeholder="Select User"
            data-testid="user-selector"
            :exclude-users="userId"
            @change="handleReassignUserChange"
          />
          <el-select
            v-else
            v-model="selectedReassignUser"
            filterable
            clearable
            style="width: 300px"
            class="pull-left"
            placeholder="Select User"
            data-testid="user-selector"
            :filter-method="onFilterAvailableUsers"
            @change="handleReassignUserChange"
          >
            <el-option
              v-for="user in availableUsersList"
              :key="user.id"
              :label="user.name"
              :value="user.id"
            >
              <div class="media">
                <div class="media-body">
                  <label>{{ user.name }}</label>
                  <small>{{ user.email }}</small>
                </div>
              </div>
            </el-option>
          </el-select>
        </div>
      </div>

      <!-- Step 3: Final Confirmation -->
      <div v-if="currentStep === 3" class="step-content">
        <h6>You are about to permanently delete {{ userName }}.</h6>

        <div v-if="userContactsCount > 0 && selectedReassignUser" class="reassignment-summary">
          <p class="mb-0">{{ userContactsCount }} contact(s) will be reassigned to <strong>{{ selectedReassignUserName }}</strong>.</p>
        </div>

        <div class="final-warning">
          <p class="text-red-10 mb-0">This action cannot be undone. Continue?</p>
        </div>
      </div>
    </div>

    <div slot="footer">
      <el-button class="pull-left"
                 icon="el-icon-arrow-left"
                 type="info"
                 data-testid="back-button"
                 v-if="currentStep >= 2"
                 @click="backStep">
          Back
      </el-button>
      <el-button @click="handleClose" :disabled="loading">
        Cancel
      </el-button>

      <el-button 
        v-if="currentStep === 1" 
        type="warning"
        :disabled="loading"
        @click="nextStep"
      >
        Continue
      </el-button>

      <el-button 
        v-if="currentStep === 2" 
        type="warning"
        :disabled="loading"
        @click="nextStep"
      >
        Continue
      </el-button>

      <el-button 
        v-if="currentStep === 3" 
        type="danger"
        :loading="loading"
        @click="confirmDeletion"
      >
        Delete User
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex'

const REASSIGN_CONTACTS_REASON_DEACTIVATED = 'deactivated'

export default {
  name: 'UserDeletionModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },

    userIdToDelete: {
      type: Number,
      required: false
    },

    isWhitelabelAccount: {
      type: Boolean,
      default: false
    },

    hasPerSeatPlanUseCase: {
      type: Boolean,
      default: false
    },

    isSuperAdmin: {
      type: Boolean,
      default: false
    },

    availableUsers: {
      type: Array,
      required: false,
      default: () => ([])
    },

    company: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  data() {
    return {
      currentStep: 1,
      loading: false,
      userContactsCount: 0,
      selectedReassignUser: null,
      filter: ''
    }
  },

  computed: {
    ...mapState(['users']),

    ...mapState('cache', ['current_company']),

    userToDelete() {
      const users = this.isSuperAdmin ? this.availableUsers : this.users
      return users.find(user => user.id === this.userIdToDelete)
    },

    userId() {
      return this.userToDelete?.id
    },

    userName() {
      return this.userToDelete?.name
    },

    selectedReassignUserName() {
      const users = this.isSuperAdmin ? this.availableUsers : this.users
      return users.find(user => user.id === this.selectedReassignUser)?.name
    },

    endOfBillingTerm() {
      return this.current_company?.end_of_billing_term
    },

    availableUsersList() {
      return this.availableUsers.filter(
        user => 
          user.id !== this.userId &&
          user.enabled &&
          (
            user.name.toLowerCase().includes(this.filter.toLowerCase()) || 
            user.email.toLowerCase().includes(this.filter.toLowerCase())
          )
        )
    }
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetModal()
        this.fetchUserContacts()
      }
    }
  },

  methods: {
    resetModal() {
      this.currentStep = 1
      this.loading = false
      this.userContactsCount = 0
      this.selectedReassignUser = null
    },

    async fetchUserContacts() {
      try {
        this.loading = true
        const apiPath = this.isSuperAdmin
          ? `/api/admin/user/${this.userIdToDelete}/contacts-count`
          : `/api/v1/user/${this.userIdToDelete}/contacts-count`

        const response = await axios.get(apiPath)
        this.userContactsCount = response.data.contacts_count || 0
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Failed to fetch user contacts',
          type: 'error',
          showClose: true
        })

        this.handleClose()
      } finally {
        this.loading = false
      }
    },

    handleReassignUserChange(user) {
      this.selectedReassignUser = user
    },

    onFilterAvailableUsers(filter) {
      this.filter = filter
    },

    nextStep() {
      if (this.currentStep === 1) {
        // If user has contacts, go to reassignment step, otherwise go to final confirmation
        if (this.userContactsCount > 0) {
          this.currentStep = 2
        } else {
          this.currentStep = 3
        }
      } else if (this.currentStep === 2) {
        this.currentStep = 3
      }
    },

    backStep() {
      if (this.currentStep === 3 && this.userContactsCount === 0) {
        this.currentStep = 1
      } else {
        this.currentStep--
      }
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },

    async confirmDeletion() {
      try {
        this.loading = true
        const reassign_contacts = this.userContactsCount > 0 && this.selectedReassignUser

        // If there are contacts to reassign, do that first
        if (reassign_contacts) {
          await this.reassignContacts()
        }

        // Delete the user
        const apiPath = this.isSuperAdmin ? '/api/admin/user/' : '/api/v1/user/'
        await axios.delete(`${apiPath}${this.userId}`, {
          data: { remove_contacts_relation: !reassign_contacts }
        })

        // Show success notification
        this.$notify({
          title: 'User Deleted',
          message: `User ${this.userName} has been deleted successfully.`,
          type: 'success',
          showClose: true
        })

        // Emit success event
        this.$emit('user-deleted', this.userToDelete)

        this.handleClose()
      } catch (error) {
        this.$root.handleErrors(error.response)
      } finally {
        this.loading = false
      }
    },

    async reassignContacts() {
      try {
        const apiPath = this.isSuperAdmin 
          ? `/api/admin/user/${this.userId}/reassign-contacts`
          : `/api/v1/user/${this.userId}/reassign-contacts`

        await axios.put(apiPath, {
          new_user_id: this.selectedReassignUser,
          reason: REASSIGN_CONTACTS_REASON_DEACTIVATED
        })
      } catch (error) {
        throw new Error('Failed to reassign contacts')
      }
    },

    handleClose() {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style lang="scss" scoped>
// Variables
$info-box-padding: 10px;
$info-box-border-radius: 4px;
$info-box-margin: 15px 0;

// Mixins
@mixin info-box($background-color, $border-color: null) {
  margin: $info-box-margin;
  padding: $info-box-padding;
  background-color: $background-color;
  border-radius: $info-box-border-radius;

  @if $border-color {
    border: 1px solid $border-color;
  }
}

@mixin warning-box($background-color, $border-color) {
  @include info-box($background-color, $border-color);
  margin-top: 20px;
  padding: 15px;
}

// Component styles
.user-deletion-modal {
  .el-dialog__body {
    padding: 20px;
  }
}

.step-content {
  min-height: 170px;
}

.learn-more {
  @include info-box(#f5f7fa);
}

.license-info {
  @include info-box(#f0f9ff, #b3d8ff);
}

.reassignment-summary {
  @include info-box(#f0f9ff);
}

.final-warning {
  @include warning-box(#fef0f0, #fbc4c4);
}
</style>
